import React, { useState } from 'react';
import { X, Save } from 'lucide-react';

export default function UserInfoPopup({ isOpen, onClose, onSave }) {
  const [classYear, setClassYear] = useState('');
  const [majors, setMajors] = useState([]);
  const [graduationMonth, setGraduationMonth] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Generate class years (current year + 4 to 2013, newer to older)
  const currentYear = new Date().getFullYear();
  const classYears = [];
  for (let year = currentYear + 4; year >= 2013; year--) {
    classYears.push(year);
  }

  // Amherst College department codes (four-character abbreviations)
  const availableMajors = [
    'Undecided', // Undecided
    'AAPI', // Asian American and Pacific Islander Studies
    'AMST', // American Studies
    'ANTH', // Anthropology
    'ARCH', // Architecture
    'ARHA', // Art and the History of Art
    'ASLC', // Asian Languages and Civilizations
    'ASTR', // Astronomy
    'BCBP', // Biochemistry and Biophysics
    'BIOL', // Biology
    'BLST', // Black Studies
    'CHEM', // Chemistry
    'CLAS', // Classics
    'COSC', // Computer Science
    'ECON', // Economics
    'EDST', // Education Studies
    'ENGL', // English
    'ENST', // Environmental Studies
    'EUST', // European Studies
    'FAMS', // Film and Media Studies
    'FREN', // French
    'FYSE', // First Year Seminar
    'GEOL', // Geology
    'GERM', // German
    'GREE', // Greek
    'HIST', // History
    'LATI', // Latin
    'LJST', // Law, Jurisprudence and Social Thought
    'LLAS', // Latin American and Latino Studies
    'MATH', // Mathematics
    'MUSI', // Music
    'NEUR', // Neuroscience
    'PHIL', // Philosophy
    'PHYS', // Physics
    'POSC', // Political Science
    'PSYC', // Psychology
    'RELI', // Religion
    'RUSS', // Russian
    'SOCI', // Sociology
    'SPAN', // Spanish
    'STAT', // Statistics
    'SWAG', // Sexuality, Women's and Gender Studies
    'THDA', // Theater and Dance
    'Interdisciplinary' // Interdisciplinary
  ];

  const handleSave = async () => {
    if (!classYear || majors.length === 0 || !graduationMonth) {
      setError('Please fill out all fields: class year, major(s), and graduation month.');
      return;
    }
    setError('');

    setIsLoading(true);
    // Timeout wrapper: close popup after 5s regardless of save outcome
    const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('Save timeout')), 5000));
    try {
      await Promise.race([onSave({ classYear, majors, graduationMonth }), timeout]);
    } catch (error) {
      console.warn('User info save skipped:', error.message);
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleMajorChange = (majorCode) => {
    setMajors(prev => {
      if (prev.includes(majorCode)) {
        return prev.filter(m => m !== majorCode);
      } else {
        return [...prev, majorCode];
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-panel max-w-md p-6">
        {/* Header */}
        <div className="mb-1 flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold text-ink">
            Tell us about yourself
          </h2>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="-mr-1 rounded-md p-1.5 text-ink-soft transition-colors hover:bg-surface-alt hover:text-ink disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mb-5 text-sm text-ink-muted">
          This helps tailor your map and recommendations.
        </p>

        {/* Content */}
        <div className="space-y-5">

          {/* Class Year Dropdown */}
          <div>
            <label htmlFor="class-year" className="label">Class year *</label>
            <select
              id="class-year"
              value={classYear}
              onChange={(e) => setClassYear(e.target.value)}
              disabled={isLoading}
              className="input disabled:opacity-50"
            >
              <option value="">Select your class year</option>
              {classYears.map(year => (
                <option key={year} value={year}>
                  Class of {year}
                </option>
              ))}
            </select>
          </div>

          {/* Graduation Month */}
          <div>
            <span className="label">What month did/will you graduate? *</span>
            <div className="flex gap-3">
              {['May', 'December'].map((month) => (
                <label
                  key={month}
                  className={`flex flex-1 cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
                    graduationMonth === month
                      ? 'border-primary bg-primary-50 text-primary'
                      : 'border-line text-ink-muted hover:bg-surface-alt'
                  }`}
                >
                  <input
                    type="radio"
                    name="graduationMonth"
                    value={month}
                    checked={graduationMonth === month}
                    onChange={(e) => setGraduationMonth(e.target.value)}
                    disabled={isLoading}
                    className="text-primary focus:ring-primary"
                  />
                  {month}
                </label>
              ))}
            </div>
          </div>

          {/* Major Selection */}
          <div>
            <span className="label">Your (intended) major(s) * — select all that apply</span>
            <div className="max-h-48 space-y-1 overflow-y-auto rounded-lg border border-line p-2">
              {availableMajors.map(majorOption => (
                <label
                  key={majorOption}
                  className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-sm text-ink transition-colors hover:bg-surface-alt"
                >
                  <input
                    type="checkbox"
                    checked={majors.includes(majorOption)}
                    onChange={() => handleMajorChange(majorOption)}
                    disabled={isLoading}
                    className="h-4 w-4 rounded border-line text-primary focus:ring-primary"
                  />
                  {majorOption}
                </label>
              ))}
            </div>
            {majors.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {majors.map((m) => (
                  <span key={m} className="badge-primary">{m}</span>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="rounded-lg border border-danger/30 bg-danger-soft p-3 text-sm text-danger">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSave}
            disabled={isLoading || !classYear || majors.length === 0 || !graduationMonth}
            className="btn-primary"
          >
            {isLoading ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                Saving…
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
